import { transition } from '@helpers/animate';
import brand, { colourWithTransparency } from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import fonts from '@helpers/fonts';
import { ButtonReset } from '@helpers/global';
import { siteWide } from '@helpers/grid';
import { Device, from, until } from '@helpers/media';
import srOnly from '@helpers/srOnly';
import { h5, link, paragraphRegular } from '@helpers/typography';
import ButtonStyles from '@stories/Components/Buttons/Button/Button.styles';
import { CommonTextInputStyles } from '@stories/Components/Misc/Forms/Inputs/Textbox/Textbox.styles';
import { m } from 'framer-motion';
import styled, { css, keyframes } from 'styled-components';
const Container = styled.footer `
  position: relative;
  overflow: hidden;
  ${fonts.gotham.regular};
  background-color: ${brand.global.black};
  color: ${brand.global.white};
`;
const Inner = styled.div `
  ${siteWide};
  position: relative;
  padding-top: ${fluid(24, 64)};
  padding-bottom: ${fluid(24, 64)};

  display: grid;
  grid-template-columns: repeat(12, 1fr);
  grid-template-rows: auto auto auto;
  gap: 64px 0;

  @media ${until(Device.DesktopSmall)} {
    gap: 16px 0;
  }
`;
const BackToTopBtn = styled.button `
  ${ButtonReset};
  position: absolute;
  right: 20px;
  top: ${fluid(24, 64)};
  width: ${fluid(60, 88)};
  height: ${fluid(60, 88)};
`;
const rotate = keyframes `
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;
const BackToTopText = styled.div `
  animation: ${rotate} 10s linear infinite;
  width: ${fluid(60, 88)};
  height: ${fluid(60, 88)};
  svg {
    width: ${fluid(60, 88)};
    height: ${fluid(60, 88)};
  }
`;
const BackToTopIcon = styled.div `
  ${transition('background-color')};
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);

  border-radius: 10000000px;
  background-color: transparent;
  width: ${fluid(35, 45)};
  height: ${fluid(35, 45)};
  display: grid;
  place-items: center;

  ${BackToTopBtn}:hover &,
  ${BackToTopBtn}.focus-ring & {
    background-color: ${brand.primary.pear.pear100};

    svg {
      color: ${brand.global.black};
    }
  }

  svg {
    ${transition('color')};
    width: ${fluid(28, 40)};
    height: ${fluid(28, 40)};
    color: ${brand.primary.pear.pear100};
    transform: rotate(-90deg) translateY(-1px);
  }
`;
const BackText = styled.span ``;
const Skew = styled.div `
  position: relative;
  width: 10px;
  height: 100%;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 3px;
    height: 100%;
    background-color: ${colourWithTransparency(brand.global.white, 0.32)};
    transform: skewX(-10deg);
  }
`;
const QuoteArea = styled.div `
  grid-column: span 9;

  display: flex;
  gap: ${fluid(12, 32)};
  align-items: center;

  @media ${until(Device.DesktopSmall)} {
    flex-direction: column;
    grid-column: span 10;
    align-items: start;
    padding-right: 20px;
  }

  ${Skew} {
    @media ${until(Device.DesktopSmall)} {
      display: none;
    }
  }
`;
const Quote = styled.div `
  --highlightColour: ${brand.primary.pear.pear100};
  ${fonts.gotham.regular};
  font-size: ${fluid(18, 32)};
  line-height: ${fluid(24, 48)};
  flex: 0 1 auto;

  span {
    ${fonts.gotham.bold};
    color: var(--highlightColour);
  }
`;
const QuoteInfo = styled.div `
  display: flex;
  flex-direction: column;

  @media ${until(Device.DesktopSmall)} {    
    padding-top: 6px;
    border-top: 1px solid ${colourWithTransparency(brand.global.white, 0.32)};
  }
`;
const QuoteProfession = styled.span `
  ${fonts.gotham.boldItalic};
  font-size: 12px;
  line-height: 22px;
  color: ${brand.primary.teal.teal50};
`;
const QuoteName = styled.span `
  ${fonts.gotham.bold};
  font-size: ${fluid(16, 20)};
  line-height: 22px;
  white-space: nowrap;
`;
const LeftColumn = styled.div `
  grid-row: 2;
  grid-column: span 4;

  @media ${until(Device.DesktopSmall)} {
    grid-column: span 12;
    border-top: 2px solid ${colourWithTransparency(brand.global.white, 0.32)};
    padding-top: 24px;
  }
`;
const LogoArea = styled.div `
  display: flex;
  gap: ${fluid(15, 20)};
  align-items: center;
  margin-bottom: ${fluid(24, 32)};

  ${Skew} {
    position: relative;
    height: ${fluid(29, 38)};
    width: 10px;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 4px;
      width: 2px;
      height: 100%;
      background-color: ${colourWithTransparency(brand.global.white, 0.32)};
      transform: skewX(-10deg);
    }
  }
`;
const MainLogo = styled.a `

  &:hover {
    svg {
      transform: scale(1.1);
    }
  }

  &.focus-ring {
    outline: auto;
    outline-offset: 3px;
    outline-width: 1px;
  }

  svg {
    ${transition('transform')};
    height: ${fluid(79, 104)};
  }
`;
const BsaLogo = styled.a `
  &:hover {
    svg {
      transform: scale(1.1);
    }
  }

  &.focus-ring {
    outline: auto;
    outline-offset: 3px;
    outline-width: 1px;
  }

  svg {
    ${transition('transform')};
    height: ${fluid(29, 38)};
  }
`;
const NewsletterContainer = styled.form ``;
const NewsletterTitle = styled.p `
  ${h5};
  margin-bottom: 16px;
  ${fonts.gotham.bold};
`;
const NewsletterContent = styled.p `
  ${paragraphRegular};
`;
const NewsletterInputContainer = styled.div `
  position: relative;
`;
const NewsletterInput = styled.input `
  ${transition('border-color')};
  ${CommonTextInputStyles};
  --borderRadius: 100px;
  --bgColour: ${brand.global.black};
  --borderColour: ${brand.global.white};
  --placeholderColour: ${brand.global.white};
  padding: 12px 60px 12px 24px;
  height: 64px;
  border-width: 2px;

  &:focus-within {
    border-color: ${brand.primary.pear.pear100};
  }

  @media ${until(Device.Tablet)} {
    height: 51px;
  }
`;
const NewsletterSubmit = styled.div `
  position: absolute;
  right: 6px;
  top: 6px;

  @media ${until(Device.Tablet)} {
    right: 4px;
    top: 4px;
  }

  ${() => ButtonStyles.Container} {
    --buttonHeight: 52px;

    @media ${until(Device.Tablet)} {
      --buttonHeight: 43px;
    }
  }
`;
const ConfirmationMessage = styled.div `
  ${paragraphRegular};
`;
const RightColumn = styled.div `
  grid-row: 2;
  grid-column: 6 / span 7;
  justify-content: end;

  @media ${until(Device.DesktopSmall)} {
    grid-row: 3;
    grid-column: span 12;
  }
`;
const FooterLinks = styled.div `
  margin: ${fluid(12, 24)} 0 ${fluid(0, 40)};

  @media ${from(Device.TabletLarge)} {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: ${fluid(16, 120)};
    margin: ${fluid(24, 24)} 0 ${fluid(36, 52)};
  }
`;
const footerLinkStyles = css `
  ${link};
  font-weight: 400;

  text-decoration-color: transparent;
  color: ${brand.global.white};

  &:hover {
    text-decoration-color: currentColor;
  }
`;
const LinkListHeader = styled.button `
  ${ButtonReset};

  color: currentColor;
  margin: 0 0 ${fluid(15, 20)};

  h2 {
    ${fonts.gotham.ultra};

    font-size: ${fluid(16, 20)};
    line-height: 1.2;
  }

  button& {
    display: flex;
    align-items: center;
    width: 100%;
    text-align: left;
    gap: 24px;
    justify-content: space-between;
    padding: 0 0 15px;
    border-bottom: 1px solid ${brand.global.white};
    padding-right: 6px;

    &:focus-visible {
      outline: auto;
      outline-offset: 5px;
      outline-width: 1px;
    }

    &[data-open='true'] svg {
      transform: rotate(180deg);
    }

    /* Mobile click styles - button should mimic press */
    @media ${until(Device.TabletLarge)} {
      ${transition('transform', 0.15)};

      &:active {
        transform: translateY(1.5px);
      }
    }

    @media ${from(Device.TabletLarge)} {
      display: none;
    }
  }

  a& {
    ${footerLinkStyles};
  }

  a&,
  span& {
    @media ${until(Device.TabletLarge)} {
      display: none;
    }
  }

  svg {
    ${transition('transform', 0.25)};

    display: block;
    width: 24px;
    color: ${brand.global.white};
  }
`;
const LinkList = styled(m.ul) `  
  column-count: 2;
  gap: ${fluid(0, 40)};

  @media ${until(Device.DesktopSmall)} {
    column-count: 1;
    overflow: hidden;
  }

  li {
    ${fonts.gotham.regular};

    font-size: ${fluid(14, 16)};
    line-height: ${fluid(18, 20)};
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 20px;
  }

  span {
    flex: 0 0 28px;
    background-color: ${brand.primary.teal.teal50};
    border-radius: 100000px;
    height: 28px;
    width: 28px;

    display: flex;
    align-items: center;
    justify-content: center;

    svg {
      width: 17px;
      height: 17px;
    }
  }

  a {
    ${footerLinkStyles};

    display: flex;
    gap: 8px;

    svg {
      width: 16px;
      transform: rotate(-90deg);
    }
  }

  @media ${from(Device.TabletLarge)} {
    height: auto !important;
    padding-bottom: 3px;
    margin-bottom: 0 !important;
    visibility: visible !important;

    li[data-mobile-only] {
      display: none;
    }
  }
`;
const BrandList = styled.div ``;
const BrandListImages = styled(m.ul) `  
  display: block;
  gap: ${fluid(0, 40)};
  column-count: 4;

  @media ${until(Device.DesktopSmall)} {
    column-count: 2;
    gap: 24px;
    overflow: hidden;
  }

  li {
    ${fonts.gotham.regular};

    font-size: ${fluid(14, 16)};
    line-height: ${fluid(18, 20)};
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 20px;
  }

  span {
    flex: 0 0 28px;
    background-color: ${brand.primary.teal.teal50};
    border-radius: 100000px;
    height: 28px;
    width: 28px;

    display: flex;
    align-items: center;
    justify-content: center;

    svg {
      width: 17px;
      height: 17px;
    }
  }

  a {
    ${footerLinkStyles};

    display: flex;
    gap: 8px;

    svg {
      width: 16px;
      transform: rotate(-90deg);
    }
  }

  @media ${from(Device.TabletLarge)} {
    height: auto !important;
    padding-bottom: 3px;
    margin-bottom: 0 !important;
    visibility: visible !important;

    li[data-mobile-only] {
      display: none;
    }
  }
`;
const BrandListImage = styled.img `
  ${transition('transform')};
  height: 56px;

  a:hover & {
    transform: scale(1.1);
  }
`;
const BaseArea = styled.div `
  grid-row: 3;
  grid-column: span 12;

  display: grid;
  grid-template-columns: repeat(12, 1fr);

  align-items: end;

  border-top: 2px solid ${colourWithTransparency(brand.global.white, 0.32)};
  padding-top: ${fluid(40, 40)};

  @media ${until(Device.DesktopSmall)} {
    grid-row: 4;
    margin-top: 20px;
    grid-template-rows: auto auto;
  }
`;
const BaseLeft = styled.div `
  grid-column: span 6;

  @media ${until(Device.DesktopSmall)} {
    grid-row: 2;
    grid-column: span 12;
  }
`;
const LegalText = styled.span `
  ${fonts.gotham.bold};
  font-size: ${fluid(12, 14)};
  line-height: ${fluid(21, 24)};
  margin-bottom: ${fluid(6, 12)};
  display: block;
`;
const LegalText2 = styled.span `
  font-size: ${fluid(12, 14)};
  line-height: ${fluid(21, 24)};
  display: block;
`;
const BaseRight = styled.div `
  grid-column: 7 / span 6;
  text-align: right;

  @media ${until(Device.DesktopSmall)} {
    grid-row: 1;
    grid-column: span 12;
    text-align: left;
  }
`;
const SocialLinks = styled.div `
  grid-column: span 3;
  margin-bottom: ${fluid(12, 24)};

  @media ${until(Device.TabletLarge)} {
    grid-column: span 1;
  }
`;
const SocialLinkList = styled.div `
  display: flex;
  justify-content: flex-end;
  align-items: flex-start;
  column-gap: 16px;

  @media ${until(Device.DesktopSmall)} {
    justify-content: flex-start;
    column-gap: 24px;
  }
`;
const SocialLink = styled.a `
  ${transition('transform')};
  color: ${brand.global.white};

  &.focus-ring {
    outline: auto;
    outline-offset: 3px;
    outline-width: 1px;
  }

  svg {
    height: ${fluid(19, 24)};
  }

  &:hover {
    transform: scale(1.1);
  }
`;
const IconImg = styled.img ``;
const LegalLinks = styled.div `
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 16px;

  @media ${until(Device.DesktopSmall)} {
    justify-content: flex-start;
    margin-bottom: 16px;
  }
`;
const LegalLink = styled.div ``;
const LegalLinkAnchor = styled.a `
  color: ${brand.global.white};
  font-size: 14px;
  line-height: 1.1em;

  &:hover {
    text-decoration: none;
  }

  &.focus-ring {
    outline: auto;
    outline-offset: 3px;
    outline-width: 1px;
  }
`;
const BackgroundImageContainer = styled.div `
  position: absolute;
  top: 10%;
  right: -25%;
  pointer-events: none;

  @media ${until(Device.DesktopSmall)} {
    display: none;
  }
`;
const BackgroundImage = styled.img `
  opacity: 0.1;
`;
const FormArea = styled.div ``;
const Form = styled.div `
  .umbraco-forms-page {
    background-color: transparent;
    padding: 0;
    margin: 0;
    --textColour: ${brand.global.white};

    .umbraco-forms-label {
      ${srOnly};
    }

    .row-fluid {
      .umbraco-forms-container {
        margin-bottom: 0;
        gap: ${fluid(24, 32)};
      }

      .umbraco-forms-field-wrapper:has(select)::after {
        height: 53px;
        width: 53px;

        @media ${until(Device.Tablet)} {
          height: 41px;
          width: 41px;
        }
      }

      select {
        --bgColour: transparent;
        --borderColour: ${brand.global.white};

        height: 64px;

        @media ${until(Device.Tablet)} {
          height: 51px;
        }

        option {
          color: ${brand.global.black};
        }
      }
    }

    .row-fluid input:not([type="file"]):not([type="submit"]) {
      --bgColour: transparent;
      --borderColour: ${brand.global.white};
      --placeholderColour: ${brand.global.white};

      ${transition('border-color')};

      height: 64px;

      &:focus-within {
        border-color: ${brand.primary.pear.pear100};
      }

      @media ${until(Device.Tablet)} {
        height: 51px;
      }
      
    }

    input.btn {
      --bgColour: ${brand.primary.pear.pear100};
      --bgHoverColour: ${brand.primary.pear.pear50};
      --bgActiveColour: ${brand.primary.pear.pear100};
      --borderColour: ${brand.primary.pear.pear100};
      --borderHoverColour: ${brand.primary.pear.pear50};
      --borderActiveColour: ${brand.primary.pear.pear100};
      --fgColour: ${brand.global.black};
      --fgHoverColour: ${brand.global.black};
      --fgActiveColour: ${brand.global.black};
    }
  }
`;
const FooterStyles = {
    BackText,
    BackToTopBtn,
    BackToTopIcon,
    BackToTopText,
    BaseArea,
    BaseLeft,
    BaseRight,
    BrandList,
    BrandListImage,
    BrandListImages,
    BsaLogo,
    Container,
    FooterLinks,
    Form,
    FormArea,
    IconImg,
    Inner,
    LeftColumn,
    LegalLink,
    LegalLinkAnchor,
    LegalLinks,
    LegalText,
    LegalText2,
    LinkList,
    LinkListHeader,
    LogoArea,
    MainLogo,
    Quote,
    QuoteArea,
    QuoteInfo,
    QuoteName,
    QuoteProfession,
    RightColumn,
    Skew,
    SocialLink,
    SocialLinkList,
    SocialLinks,
    NewsletterContainer,
    NewsletterTitle,
    NewsletterContent,
    NewsletterInputContainer,
    NewsletterInput,
    NewsletterSubmit,
    ConfirmationMessage,
    BackgroundImageContainer,
    BackgroundImage,
};
export default FooterStyles;
